import _ from 'underscore';
import $ from 'jquery';
import ObjectUtil from '@core/util/object';
import Cookie from '@component/common/src/util/Cookie';
import RegistryUtil from '@component/common/src/util/RegistryUtil';
import { RVI_STORAGE_NAME } from '@component/common/src/util/Constants';
import Storage from '@component/common/src/util/ClientSideStorage';
import Constants from './Constants';
import ProsEvents from '../util/ProsEvents';

const Util = {

  failureEvent(opts) {
    const { requester, zone } = opts;
    return `${requester}-${zone}${ProsEvents.failure}`;
  },

  countryCode() {
    return Cookie.get(Constants.SHIPPING_COUNTRY_COOKIE_NAME);
  },

  customerId() {
    return Cookie.get(Constants.CUSTOMER_ID_COOKIE_NAME);
  },

  /**
     * input => output
     * undefined => undefined
     * PDP_ZONE_A => PDP_ZONE_A
     * PDP_ZONE_A|PDP_ZONE_B => PDP_ZONE_B
     * @param zones
     * @returns {*}
     */
  fallbackZone(zones) {
    if (!zones) {
      return zones;
    }

    return zones.split('|')[0];
  },

  guid() {
    const S4 = () => {
      const d = new Date();
      // eslint-disable-next-line no-bitwise
      return (((d.getTime() + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    };

    return (S4() + S4() + S4() + S4() + S4() + S4() + S4() + S4());
  },

  header(headerText) {
    let header = decodeURIComponent(headerText.replace(/\+/g, ' '));
    const headerVariables = header.match(/<<(.*?)>>/g);
    const customerNameLabel = 'CustomerName';
    const username = Util.userNm();
    if (headerVariables) {
      headerVariables.forEach((dynamicStructure) => {
        let dynamicVariable = dynamicStructure.split('::')[0].replace(/<</, '');
        const defaultVariableValue = dynamicStructure.split('::')[1].replace(/>>/, '');

        if (dynamicVariable.indexOf(customerNameLabel) !== -1) {
          if (username) {
            dynamicVariable = dynamicVariable.replace(/CustomerName/, username);
          } else {
            dynamicVariable = defaultVariableValue;
          }
        } else {
          dynamicVariable = defaultVariableValue;
        }
        header = header.replace(dynamicStructure, dynamicVariable);
      });
    }

    header = header.replace(/v_username/g, username);
    return header;
  },

  /**
     * call this function on demand in case there is no RTD generated cookie visitor
     * ID and a RTD event needs to be sent
     */
  visitorId() {
    let rtdCookie = $.trim(Cookie.get(Constants.RTD_COOKIE_NAME) || '');

    // if session hasn't been started or the user has an invalid visitor Id
    if (!this.validVisitorId(rtdCookie)) {
      const visitorId = this.guid();
      const d = new Date();
      d.setFullYear(d.getFullYear() + 30);

      // push Expiration Date of Cookie
      Cookie.setExpires(d.toUTCString());
      Cookie.set(Constants.RTD_COOKIE_NAME, visitorId); // key/value
      rtdCookie = Cookie.get(Constants.RTD_COOKIE_NAME);
    }

    return rtdCookie;
  },


  allZonesRecommendationEmpty(zonesJson, config) {
    if (!zonesJson) {
      return true;
    }
    const zones = config.zone && config.zone.split('|');
    return _.find(zones, zone => !_.isEmpty(zonesJson[`${config.requester}-${zone}`])) === undefined;
  },


  /**
     *
     * @param opts Object
     * {fallbackEnabled : true/false, }
     * @param response Object
     * @returns {boolean}
     *
     * if json is undefined, we need fcc fallback.
     * or
     * if json is defined but all zones are empty, we need fcc fallback.
     */

  needFccRecommendation({ config, properties, response }) {
    if (!properties.fccFallbackEnabled) {
      return false;
    }
    return !response || (this.allZonesRecommendationEmpty(response.recommendationsOnAllZones, config) && _.isEmpty(response.recommendedItems));
  },

  /**
     * input => output
     * undefined => undefined
     * PDP_ZONE_A => ""
     * PDP_ZONE_A|PDP_ZONE_B => "PDP_ZONE_B"
     *
     * @param zones
     * @returns {*}
     */
  otherThanFallbackZones(zones) {
    if (!zones) {
      return zones;
    }

    return zones.split('|').splice(1).join('|');
  },

  /**
     * name=john&city=sf  => {name:john, city:sf}
     * @param parameters
     * @returns {{}}
     */
  paramsToObj(parameters) {
    if (!parameters) {
      return parameters;
    }
    const ret = {};
    const seg = parameters.replace(/^\?/, '').split('&');
    const len = seg.length;
    let i = 0;
    for (; i < len; i += 1) {
      if (seg[i]) {
        const [key, value] = seg[i].split('=');
        ret[key] = value;
      }
    }
    return ret;
  },

  /**
     * Pass it http://someurl or https://someurl
     * it will return //someurl
     * @param url
     * @returns {*}
     */
  protocolLessUrl(url) {
    return url ? url.replace(/http[s]*:/, '') : url;
  },

  stateCode() {
    return Cookie.get(Constants.STATE_COOKIE_NAME, Constants.MISCGCS);
  },

  userNm() {
    return Cookie.get('UserName', 'MISCGCs');
  },

  uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      // eslint-disable-next-line no-bitwise
      const r = Math.random() * 16 | 0;
      // eslint-disable-next-line no-bitwise,no-mixed-operators
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  },

  validVisitorId(visitorId) {
    return visitorId && visitorId.length < 100;
  },

  zipCode() {
    return Cookie.get(Constants.ZIP_CODE_COOKIE_NAME, Constants.MISCGCS);
  },

  encodedChoiceId({
    controlGroupId, deliveryId, headerId, headerText, categoryId,
  }) {
    return `${controlGroupId}-${deliveryId}@${headerId}@${headerText}$${categoryId}`;
  },

  parseChoiceId(encodedChoiceId) {
    if (!encodedChoiceId) {
      return false;
    }
    const [choiceId, headerId, headerText] = encodedChoiceId.split(Constants.HEADER_SEPARATOR);
    if (choiceId) {
      return {
        // eslint-disable-next-line no-mixed-operators
        choiceId: headerId && choiceId.substring(3) || choiceId.split('$')[0].substring(3),
        headerId: headerId || '',
        // eslint-disable-next-line no-mixed-operators
        headerText: headerText && this.header(headerText.split('$')[0]) || Constants.DEFAULT_HEADER_TEXT,
      };
    }
    return false;
  },

  isMobile() {
    const deviceType = Cookie.get('DT', 'MISCGCs');
    if (_.isUndefined(deviceType)) {
      return navigator.userAgent.match(/Mobi/i);
    }
    return (deviceType !== 'PC');
  },

  getCMPageForContent(zone) {
    const context = zone.substring(0, zone.length - 2);
    let pageName = '';
    switch (context) {
      case 'HP_ZONE':
        pageName = 'homepage';
        break;
      case 'C2FLEX_ZONE':
        pageName = 'cat_splash';
        break;
      default:
        pageName = 'homepage';
        break;
    }
    return pageName;
  },

  getRecentlyViewedItems() {
    const prodIds = Storage.getPersistent(RVI_STORAGE_NAME);
    if (_.isString(prodIds)) {
      return prodIds.split(',');
    }
    return [];
  },

  isSmallSizeScreen() {
    return $(window).width() < Constants.BREAK_POINTS.SMALL;
  },

  isLargeSizeScreen() {
    return $(window).width() >= Constants.BREAK_POINTS.MEDIUM;
  },

  internationalOrRegistryMode() {
    return Cookie.isInternationalMode() || RegistryUtil.isRegistry();
  },

  toCamelCase(str) {
    // Lower cases the string
    return str.toLowerCase()
    // Replaces any - or _ characters with a space
      .replace(/[-_]+/g, ' ')
      // Removes any non alphanumeric characters
      .replace(/[^\w\s]/g, '')
      // Uppercases the first character in each group immediately following a space
      // (delimited by spaces)
      .replace(/ (.)/g, $1 => $1.toUpperCase())
      // Removes spaces
      .replace(/ /g, '');
  },

  toCapitalize(str) {
    return str.toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  },

  toIdValue(str) {
    return str.trim().replace(/\s+/g, '_').toUpperCase();
  },

  parseMultizoneContext(context) {
    let zonePreString = false;
    return context.split('|').map((zone) => {
      if (!zonePreString && zone.length > 1) {
        zonePreString = zone.substring(0, zone.length - 1);
        // eslint-disable-next-line no-param-reassign
        zone = zone.replace(zonePreString, '');
      }
      return zonePreString + zone;
    }).join('|');
  },

  hasMultiZoneContext(context) {
    const multizone = context ? context.match(/([A-Z_]+)(\|([A-Z_]+))+/gi) : false;
    return multizone ? multizone.length > 0 : false;
  },

  generateChannelToken() {
    return Math.random(0).toString(36).substr(2);
  },


  escapeQuotes(unsafe) {
    // eslint-disable-next-line no-useless-escape
    return unsafe.replace(/(['"])/g, '\$1');
  },

  setATBCookies(response) {
    const expires = new Date(new Date().getTime() + Constants.COOKIES.THIRTHY_DAYS_IN_MS);

    this.setCookie(Constants.COOKIES.USER_ID, response.userId, expires);
    this.setCookie(Constants.COOKIES.MACHINE_ID, response.machineId, expires);
    this.setCookie(Constants.COOKIES.BAG_GUID, response.bagGuid, expires);

    return true;
  },

  setCookie(cookieName, cookieValue, expires) {
    Cookie.set(cookieName, cookieValue, undefined, {
      expires,
    });
  },

  getShop5ExploreAttributes(productModel) {
    const attr = [];
    let prosControlGroupId;
    let batchId;
    let headerId;
    const choiceId = ObjectUtil.hasChildProperty(productModel, 'attributes.choiceId') ? productModel.attributes.choiceId : '';
    const prosPos = `Pos${ObjectUtil.hasChildProperty(productModel, 'attributes.position') ? productModel.attributes.position : ''}`;

    if (choiceId && choiceId.split('-') && choiceId.split('-').length) {
      prosControlGroupId = choiceId.split('-')[0].substring(3, 9);
    }

    if (choiceId) {
      // eslint-disable-next-line no-useless-escape
      batchId = choiceId.match((/\-([\w\d\-]+)/));
      headerId = choiceId.match(/@(H.+?)@/);
    }

    if (prosPos && prosControlGroupId) {
      batchId = (batchId && batchId[1]) ? batchId[1] : '';
      headerId = (headerId && headerId.length) ? headerId[1] : '';
      const sourceCatId = ObjectUtil.hasChildProperty(productModel, 'attributes.product.relationships.taxonomy.defaultCategoryId') ? productModel.attributes.product.relationships.taxonomy.defaultCategoryId : '';

      const ids = [prosPos, prosControlGroupId];
      if (batchId) {
        ids.push(batchId);
      }
      if (headerId) {
        ids.push(headerId);
      }
      if (sourceCatId) {
        ids.push(sourceCatId);
      }

      attr[1] = ids.join(' | ');
    }
    attr[13] = productModel.attributes.product.detail.flags.bigTicketItem === true ? 'Big Ticket' : undefined;
    attr[17] = productModel.attributes.product.detail.flags.isStoreOnlyProductOnline ? 'store_only_product' : undefined;
    attr[18] = productModel.attributes.product.availability.available ? undefined : 'Product Unavailable';
    attr[21] = productModel.attributes.product.imagery.videoId !== undefined ? 'ENHANCED_FEATURES_VIDEO' : undefined;
    attr[30] = productModel.attributes.product.detail.flags.PDPColorized === true ? 'PDP Colorized' : 'PDP Non Colorized';
    attr[31] = productModel.attributes.product.detail.additionalImagesCount;

    return _.rest(attr);
  },

};

export default Util;
